import { Button, Group, Modal, Paper, Table, Text } from "@mantine/core";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { formatIsoDate } from "../../lib/date.js";
import { useZielrunde, useZielrundeEinfrieren } from "../../hooks/useKpi.js";

/**
 * Kopfbereich des Ziele-Reiters: Zustand der Zielrunde (offen/eingefroren),
 * Pflegestand je Service Lead und — nur für die Segment-Führung — der Knopf
 * zum Einfrieren.
 *
 * Erscheint in BEIDEN Zweigen von `TargetsPanel` (`ZieleNurLesen` und
 * `ZielePflegen`): auch wer nur liest (Service Lead), muss sehen, ob die
 * Runde eingefroren ist — das ist der Bezugspunkt für die eigenen Zahlen.
 * Nur `darfEinfrieren` schaltet den Knopf frei.
 */
export function ZielrundeKopf({
  segmentId,
  year,
  darfEinfrieren,
}: {
  segmentId: string;
  year: number;
  darfEinfrieren: boolean;
}) {
  const { t } = useTranslation();
  const { data } = useZielrunde(segmentId, year);
  const einfrieren = useZielrundeEinfrieren();
  const [modalOpen, setModalOpen] = useState(false);

  // Namen der Leads ohne Freigabe — Grundlage für die Warnung im
  // Bestätigungs-Modal. `useMemo`, sonst kippt `react-hooks/exhaustive-deps`
  // bei jedem Render ein neues Array aus (Lint-Gate, --max-warnings 0).
  const offeneNamen = useMemo(
    () =>
      (data?.leads ?? [])
        .filter((lead) => lead.freigegebenAt === null)
        .map((lead) => lead.fullName),
    [data],
  );

  if (!data) return null;

  // Item 1 des Reviews: Das Einfrieren ist unumkehrbar (kein Auftau-Endpunkt,
  // s. Zielrunde-Spec) — der Bestätigungsdialog erscheint deshalb IMMER,
  // nicht nur bei offenen Freigaben. Ausgerechnet der harmlos aussehende Fall
  // „alle haben freigegeben" fror sonst mit einem einzigen Klick ein.
  const handleEinfrierenClick = () => {
    setModalOpen(true);
  };

  return (
    <Paper p="md" withBorder mb="md">
      <Group justify="space-between" align="flex-start" wrap="wrap">
        <div>
          <Text fw={600} c={data.status === "eingefroren" ? "dimmed" : "green"}>
            {data.status === "eingefroren"
              ? data.frozenByName
                ? t("pages.kpi.zielrunde.titelEingefroren", {
                    datum: formatIsoDate(data.frozenAt),
                    name: data.frozenByName,
                  })
                : // Item 3 des Reviews: frozenByName kann null sein (gelöschtes
                  // Konto) — ein eigener Text statt „…durch " mit hängendem
                  // „durch".
                  t("pages.kpi.zielrunde.titelEingefrorenOhneName", {
                    datum: formatIsoDate(data.frozenAt),
                  })
              : t("pages.kpi.zielrunde.titelOffen")}
          </Text>
          {data.ohneLead.beauftragungen > 0 && (
            <Text size="xs" c="dimmed" mt={4}>
              {t("pages.kpi.zielrunde.ohneLead", {
                beauftragungen: data.ohneLead.beauftragungen,
                mitZiel: data.ohneLead.mitZiel,
              })}
            </Text>
          )}
        </div>
        {darfEinfrieren && data.status === "offen" && (
          <Button
            onClick={handleEinfrierenClick}
            loading={einfrieren.isPending}
          >
            {t("pages.kpi.zielrunde.einfrieren")}
          </Button>
        )}
      </Group>

      {data.leads.length > 0 && (
        <Table mt="md" withColumnBorders>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t("pages.kpi.zielrunde.spalteLead")}</Table.Th>
              <Table.Th>{t("pages.kpi.zielrunde.spalteGepflegt")}</Table.Th>
              <Table.Th>{t("pages.kpi.zielrunde.spalteFreigabe")}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {data.leads.map((lead) => {
              // Item 2 des Reviews: „3 / 5" ohne Hervorhebung lässt rechnen.
              // Wer beim Vergleich klemmt (weniger Ziele als Beauftragungen),
              // wird sichtbar markiert statt nur gezählt.
              const klemmt = lead.mitZiel < lead.beauftragungen;
              return (
                <Table.Tr key={lead.employeeId}>
                  <Table.Td>{lead.fullName}</Table.Td>
                  <Table.Td>
                    <Text c={klemmt ? "orange" : undefined} fw={klemmt ? 600 : undefined}>
                      {`${lead.mitZiel} / ${lead.beauftragungen}`}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    {lead.freigegebenAt ? (
                      formatIsoDate(lead.freigegebenAt)
                    ) : (
                      <Text size="sm" c="dimmed">
                        –
                      </Text>
                    )}
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      )}

      <Modal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title={t("pages.kpi.zielrunde.einfrieren")}
        size="sm"
      >
        <Text size="sm" mb="md">
          {offeneNamen.length > 0
            ? t("pages.kpi.zielrunde.offeneFreigaben", {
                namen: offeneNamen.join(", "),
              })
            : t("pages.kpi.zielrunde.bestaetigungAlleFreigegeben")}
        </Text>
        <Group justify="flex-end">
          <Button variant="default" onClick={() => setModalOpen(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            color={offeneNamen.length > 0 ? "orange" : undefined}
            loading={einfrieren.isPending}
            onClick={() =>
              einfrieren.mutate(
                { segmentId, year },
                { onSuccess: () => setModalOpen(false) },
              )
            }
          >
            {offeneNamen.length > 0
              ? t("pages.kpi.zielrunde.trotzdemEinfrieren")
              : t("pages.kpi.zielrunde.einfrieren")}
          </Button>
        </Group>
      </Modal>
    </Paper>
  );
}
